import React, { Suspense } from 'react';
import './styles/main.scss';
import { BrowserRouter, Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { CompatRouter } from 'react-router-dom-v5-compat';
import { UserProvider } from '@components/contexts/UserProvider';
import ScopedCssBaseline from '@mui/material/ScopedCssBaseline';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { routes } from 'routes';
import { PermissionsProvider } from '@components/contexts/PermissionsProvider';

const theme = createTheme({
	typography: {
		fontFamily: 'var(--font-primary)',
	},
	components: {
		MuiCssBaseline: {
			styleOverrides: {
				'@font-face': {
					fontFamily: 'var(--font-primary)',
				},
			},
		},
	},
});

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			refetchOnMount: false,
			refetchOnWindowFocus: false,
		},
	},
});

const App = () => {
	window.addEventListener('storage', function (e) {
		// Check if the userRole has changed and not exists impersonate key
		if (e.storageArea === localStorage && e.key === 'token' && !localStorage.getItem('impersonate')) {
			if (e.oldValue !== e.newValue && e.oldValue !== null) {
				// Go to logout page
				window.location.href = '/logout';
			}
		}
	});

	return (
		<Suspense fallback="loading">
			<QueryClientProvider client={queryClient}>
				<BrowserRouter>
					<CompatRouter>
						{/* for react-router-dom v6 compatibility with v5 */}
						<UserProvider>
							<PermissionsProvider>
								<ThemeProvider theme={theme}>
									{/* for add custom style to Material components */}
									<ScopedCssBaseline /> {/* for add custom style to Material components only to the children */}
									<Switch>
										{routes.map((route) => {
											const Component = route.component;
											const is404 = route.url === '*';
											if (is404) {
												return <Route key={route.name} render={() => <Component />} />;
											}

											return (
												<Route
													key={route.name}
													path={route.url}
													exact
													render={() => {
														if (route.type === 'private') {
															return (
																<PrivateWrapper>
																	<Component />
																</PrivateWrapper>
															);
														}
														return <Component />;
													}}
												/>
											);
										})}
									</Switch>
								</ThemeProvider>
							</PermissionsProvider>
						</UserProvider>
					</CompatRouter>
				</BrowserRouter>
			</QueryClientProvider>
		</Suspense>
	);
};

const PrivacyToAcceptWrapper = ({ children }) => {
	const hasAcceptedPrivacyPolicy = localStorage.getItem('has_accepted_privacy_policy') === 'true';

	const { pathname } = useLocation();

	if (!hasAcceptedPrivacyPolicy && pathname !== '/privacy-policy-to-accept') {
		return <Redirect to="/privacy-policy-to-accept" />;
	}
	return children;
};

const PrivateWrapper = ({ children }) => {
	return <PrivacyToAcceptWrapper>{children}</PrivacyToAcceptWrapper>;
};

export default App;
